import * as React from 'react'

// Module-level singleton — only one live preview mounts at a time (~1 GL context, not N).
// React 17 codebase, so useState/useEffect subscription instead of useSyncExternalStore.

const DEFAULT_DELAY_MS = 200

type Listener = () => void

const listeners = new Set<Listener>()
let activeId: string | undefined = undefined
let pending: { id: string; timer: ReturnType<typeof setTimeout> } | null = null

const setActive = (id: string | undefined): void => {
  if (activeId === id) return
  activeId = id
  listeners.forEach(l => l())
}

const clearPending = (): void => {
  if (pending) {
    clearTimeout(pending.timer)
    pending = null
  }
}

interface IUseActiveLiveMapSlotResult {
  isActive: boolean
  scheduleActivate: () => void
  cancelActivate: () => void
}

interface IOptions {
  /** No-op when false (skip promotion on touch devices). */
  enabled?: boolean
  /** Hover dwell-time before promoting (ms). */
  delayMs?: number
}

export function useActiveLiveMapSlot(
  itemId: string,
  { enabled = true, delayMs = DEFAULT_DELAY_MS }: IOptions = {},
): IUseActiveLiveMapSlotResult {
  const [currentActiveId, setCurrentActiveId] = React.useState(() => activeId)
  React.useEffect(() => {
    const listener = (): void => setCurrentActiveId(activeId)
    listeners.add(listener)
    // Re-sync in case the singleton changed between initial useState read and effect.
    listener()
    return () => {
      listeners.delete(listener)
    }
  }, [])
  const isActive = enabled && currentActiveId === itemId

  const scheduleActivate = React.useCallback((): void => {
    if (!enabled) return
    if (activeId === itemId) return
    if (pending?.id === itemId) return
    clearPending()
    pending = {
      id: itemId,
      timer: setTimeout(() => {
        pending = null
        setActive(itemId)
      }, delayMs),
    }
  }, [itemId, enabled, delayMs])

  const cancelActivate = React.useCallback((): void => {
    if (pending?.id === itemId) {
      clearPending()
    }
    if (activeId === itemId) {
      setActive(undefined)
    }
  }, [itemId])

  // Release slot on unmount so the next card's hover can claim it cleanly.
  React.useEffect(
    () => () => {
      if (pending?.id === itemId) {
        clearPending()
      }
      if (activeId === itemId) {
        setActive(undefined)
      }
    },
    [itemId],
  )

  return { isActive, scheduleActivate, cancelActivate }
}
